import React, { useEffect, useMemo, useRef, useState } from "react";
import { ACTION_RULES, ACTION_TYPES, actionLabel, canRolePerform, roleClass, roleLabel, roleSide } from "../roles";

export default function ActionForm({
  players,
  actions = [],
  round,
  phase,
  draft,
  pickField,
  onTypeChange,
  onPickRequest,
  onFieldClear,
  onSubmit,
  submitting,
}) {
  const [formError, setFormError] = useState("");
  const [target2Id, setTarget2Id] = useState("");
  // Захист від повторного автозбереження того самого драфту (напр. якщо
  // ефект перерахується з тих самих значень поки триває запит).
  const lastSubmittedRef = useRef("");

  const rule = ACTION_RULES[draft.type] || {};
  const playerById = useMemo(
    () => new Map(players.map((p) => [String(p.id), p])),
    [players]
  );

  const actor = draft.actorId ? playerById.get(String(draft.actorId)) : null;
  const target = draft.targetId ? playerById.get(String(draft.targetId)) : null;
  const target2 = target2Id ? playerById.get(String(target2Id)) : null;

  // Для волоцюги: хто цієї ночі вже приходив (обирав ціллю) того самого
  // гравця, якого волоцюга обрав для ночівлі. Волоцюга — останній крок
  // нічної черги, тож на момент його вибору решта нічних дій цього раунду
  // вже записана в журналі.
  const nightVisitors = useMemo(() => {
    if (draft.type !== "witness" || !target) return [];
    return actions.filter(
      (a) =>
        a.type !== "witness" &&
        a.phase === "night" &&
        Number(a.round) === Number(round) &&
        a.targetId != null &&
        String(a.targetId) === String(target.id)
    );
  }, [draft.type, target, actions, round]);

  useEffect(() => {
    if (draft.type !== "compare") {
      setTarget2Id("");
    }
  }, [draft.type]);

  function validateAction() {
    if (rule.needsActor && !actor) {
      return "Оберіть гравця, який виконує дію.";
    }

    if (rule.needsTarget && !target) {
      return "Оберіть гравця, якого стосується дія.";
    }

    if (actor && rule.actorMustBeAlive && !actor.alive) {
      return "Мертвий гравець не може виконувати дію.";
    }

    if (target && rule.targetMustBeAlive && !target.alive) {
      return "Не можна вибрати гравця, який уже вибув.";
    }

    if (actor && rule.allowedAbility && !canRolePerform(actor.role, draft.type)) {
      return "Ця роль не має права виконувати цю дію.";
    }

    if (actor && target && !rule.allowSelfTarget && actor.id === target.id) {
      if (draft.type === "kill") return "Не можна вбити самого себе.";
      if (draft.type === "check") return "Не можна перевіряти самого себе.";
      return "Не можна вибрати самого себе як ціль.";
    }

    if (draft.type === "compare") {
      if (!target2) {
        return "Оберіть другого гравця для порівняння.";
      }
      if (target && target.id === target2.id) {
        return "Оберіть двох різних гравців для порівняння.";
      }
      if (actor && (actor.id === target.id || actor.id === target2.id)) {
        return "Журналіст не може порівнювати самого себе.";
      }
    }

    if (rule.uniqueActorTarget) {
      const alreadyDone = actions.some(
        (a) =>
          a.type === draft.type &&
          String(a.actorId) === String(draft.actorId) &&
          String(a.targetId) === String(draft.targetId)
      );

      if (alreadyDone) {
        return "Ця дія над цим гравцем уже була виконана. Повтор заборонений.";
      }
    }

    if (rule.uniqueActorPerRound) {
      const alreadyDone = actions.some(
        (a) =>
          a.type === draft.type &&
          String(a.actorId) === String(draft.actorId) &&
          Number(a.round) === Number(round)
      );

      if (alreadyDone) {
        return "Цей гравець уже виконав цю дію в цьому раунді.";
      }
    }

    return null;
  }

  // Записуємо дію автоматично, щойно вибрані всі обов'язкові поля — без
  // окремої кнопки "Додати в журнал". Спрацьовує лише один раз на кожен
  // завершений драфт (actor+target+type), тож повторний рендер з тими самими
  // значеннями (напр. поки триває запит) нічого не відправляє вдруге.
  useEffect(() => {
    if (submitting) return;

    const hasRequiredActor = !rule.needsActor || Boolean(actor);
    const hasRequiredTarget = !rule.needsTarget || Boolean(target);
    const hasRequiredTarget2 = draft.type !== "compare" || Boolean(target2);
    if (!hasRequiredActor || !hasRequiredTarget || !hasRequiredTarget2) {
      setFormError("");
      return;
    }

    const draftKey = `${draft.type}:${draft.actorId || ""}:${draft.targetId || ""}:${target2Id || ""}`;
    if (lastSubmittedRef.current === draftKey) return;

    const validationError = validateAction();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    lastSubmittedRef.current = draftKey;

    const note =
      draft.type === "compare" && target && target2
        ? `Порівняння: ${target.name} та ${target2.name} — ${
            roleSide(target.role) === roleSide(target2.role)
              ? "З однієї команди"
              : "З різних команд"
          }`
        : null;

    Promise.resolve(
      onSubmit({
        type: draft.type,
        actorId: draft.actorId || null,
        targetId: draft.targetId || null,
        round,
        phase,
        note,
      })
    ).then(() => {
      setTarget2Id("");
    }).catch(() => {
      // Дозволяємо повторну спробу для того самого драфту, якщо запит впав.
      lastSubmittedRef.current = "";
      // Помилку вже показує GameScreen через свій error-стан.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.type, draft.actorId, draft.targetId, target2Id, submitting]);

  return (
    <div className="action-form">
      <h3>Записати дію</h3>

      <label className="field">
        <span>Тип дії</span>
        <select value={draft.type} onChange={(e) => onTypeChange(e.target.value)}>
          {ACTION_TYPES.map((action) => (
            <option key={action.id} value={action.id}>
              {action.label}
            </option>
          ))}
        </select>
      </label>

      {rule.needsActor && (
        <div className="field">
          <span>Хто виконав</span>
          <PickerField
            player={actor}
            active={pickField === "actor"}
            placeholder="Тапніть картку гравця в списку"
            onActivate={() => onPickRequest("actor")}
            onClear={() => onFieldClear("actor")}
          />
        </div>
      )}

      {rule.needsTarget && (
        <div className="field">
          <span>{draft.type === "compare" ? "Перший гравець (для порівняння)" : "Кого стосується"}</span>
          <PickerField
            player={target}
            active={pickField === "target"}
            placeholder="Тапніть картку гравця в списку"
            onActivate={() => onPickRequest("target")}
            onClear={() => onFieldClear("target")}
          />
        </div>
      )}

      {draft.type === "compare" && (
        <label className="field">
          <span>Другий гравець (для порівняння)</span>
          <select
            value={target2Id}
            onChange={(e) => setTarget2Id(e.target.value)}
          >
            <option value="">-- Оберіть другого гравця --</option>
            {players
              .filter(
                (p) =>
                  p.alive &&
                  String(p.id) !== String(draft.actorId) &&
                  String(p.id) !== String(draft.targetId)
              )
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      )}

      {draft.type === "compare" && target && target2 && (
        <div
          className={`compare-result-hint ${
            roleSide(target.role) === roleSide(target2.role)
              ? "compare-result-same"
              : "compare-result-diff"
          }`}
        >
          🔎 {target.name} та {target2.name} —{" "}
          <strong>
            {roleSide(target.role) === roleSide(target2.role)
              ? "З ОДНІЄЇ КОМАНДИ"
              : "З РІЗНИХ КОМАНД"}
          </strong>
        </div>
      )}

      {draft.type === "witness" && target && (
        <div className="witness-visitors-hint">
          {nightVisitors.length > 0 ? (
            <>
              <p>
                🎒 Цієї ночі до <strong>{target.name}</strong> вже приходили:
              </p>
              <ul className="witness-visitors-list">
                {nightVisitors.map((a) => {
                  const visitor = playerById.get(String(a.actorId));
                  return (
                    <li key={a.id}>
                      {visitor ? visitor.name : "Невідомий гравець"}
                      {visitor && (
                        <span className={roleClass(visitor.role)}> ({roleLabel(visitor.role)})</span>
                      )}
                      {" — "}
                      {actionLabel(a.type)}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p>
              🎒 Цієї ночі до <strong>{target.name}</strong> ще ніхто не приходив.
            </p>
          )}
        </div>
      )}

      {formError && <p className="error-text form-inline-error">{formError}</p>}

      <p className="hint-text action-form-autosave-hint">
        {submitting
          ? "Зберігаємо…"
          : "Дія записується в журнал автоматично, щойно вибір завершено."}
      </p>
    </div>
  );
}

// Кнопка-поле замість <select> — на телефоні натискання на картку гравця
// в PlayerBoard набагато швидше за прокрутку випадаючого списку, особливо
// коли за столом 10+ людей.
function PickerField({ player, active, placeholder, onActivate, onClear }) {
  return (
    <div className={`picker-field ${active ? "picker-field-active" : ""}`}>
      <button type="button" className="picker-field-btn" onClick={onActivate}>
        {player ? (
          <>
            <span className="picker-field-chip-name">{player.name || "Без імені"}</span>
            <span className={`picker-field-chip-role ${roleClass(player.role)}`}>
              {roleLabel(player.role)}
            </span>
          </>
        ) : (
          <span className="picker-field-placeholder">
            {active ? "Оберіть гравця в списку нижче/вище…" : placeholder}
          </span>
        )}
      </button>
      {player && (
        <button
          type="button"
          className="picker-field-clear"
          onClick={onClear}
          aria-label="Очистити вибір"
        >
          ×
        </button>
      )}
    </div>
  );
}
